import React, { useCallback, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_CHAT_ORDER_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { listChatOrders } from "../common/warrantyApi";
import { usePagedList } from "../common/usePagedList";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatMinor } from "../common/format";
import { coveredLabel, deliveryKindLabel, partLabel, partOrderStatusLabel } from "../common/opsLabels";
import { PAGE_SIZE, STATUS_FILTERS, filtersFromParams, filtersToParams, listQuery } from "../common/chatOrderList";
import "../warranty.css";

const chatOrderUrl = (item) => `${WARRANTY_CHAT_ORDER_URL}/${encodeURIComponent(item.partOrderId)}`;
// why: a status the screen does not know gets a neutral badge; a server value never becomes a class.
const STATUS_CLASS = {
  placed: "is-part-placed",
  paid: "is-part-paid",
  offered: "is-part-offered",
  placing: "is-part-placing",
  awaiting_payment: "is-part-awaiting_payment",
  refunding: "is-part-refunding",
  refused: "is-part-refused",
  refunded: "is-part-refunded",
  cancelled: "is-part-cancelled",
};
const statusClass = (status) =>
  Object.prototype.hasOwnProperty.call(STATUS_CLASS, status) ? STATUS_CLASS[status] : "is-past";
const dash = (value) => (value === null || value === undefined || value === "" ? "-" : value);

// Every battery and charger order the support bot placed in chat. Read only, the same permissions as
// Assure customers. No name, phone or address: the frame, the ticket and the OMS order code find the
// rest in the OMS's own screens.
const ChatOrders = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.OPS, WARRANTY_PERMISSION.VIEW]);
  useWarrantyNav(allowed);
  const navigate = useNavigate();

  // Filters live in the URL, so Back from an order returns to the same list.
  const [params, setParams] = useSearchParams();
  const paramText = params.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(paramText)), [paramText]);
  const { status, from, to } = filters;
  const setFilter = (patch) => setParams(filtersToParams({ ...filters, ...patch }), { replace: true });

  const fetchPage = useCallback(
    (cursor) => listChatOrders(accessToken, listQuery({ status, from, to }, cursor, PAGE_SIZE)),
    [accessToken, status, from, to]
  );
  const list = usePagedList(fetchPage, allowed);

  return (
    <WarrantyPage
      title="Chat orders"
      sub="Battery and charger orders the support bot placed in chat. Market IN. Read only. Open one for its history."
      allowed={allowed}
      deniedTitle="You do not have access to chat orders"
      deniedText="Ask an admin to give you the Warranty Operations or Warranty view permission."
      actions={
        <Button className="Addbtn" onClick={() => list.reload(false)} disabled={list.status === "loading"}>
          Reload
        </Button>
      }
    >
      <div className="formloginset warranty-list-filters">
        <Form.Group controlId="warrantyChatOrdersStatus">
          <Form.Label>Status</Form.Label>
          <Form.Select value={status} onChange={(event) => setFilter({ status: event.target.value })}>
            <option value="">Any</option>
            {STATUS_FILTERS.map((key) => (
              <option key={key} value={key}>
                {partOrderStatusLabel(key)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyChatOrdersFrom">
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={from}
            max={to || undefined}
            onChange={(event) => setFilter({ from: event.target.value })}
          />
        </Form.Group>
        <Form.Group controlId="warrantyChatOrdersTo">
          <Form.Label>To</Form.Label>
          <Form.Control
            type="date"
            value={to}
            min={from || undefined}
            onChange={(event) => setFilter({ to: event.target.value })}
          />
        </Form.Group>
      </div>

      {list.status === "loading" && <Loading label="Loading chat orders" />}
      {list.status === "error" && <ErrorState message={list.message} onRetry={() => list.reload()} />}
      {list.status === "ready" && list.items.length === 0 && (
        <EmptyState title="No chat orders match these filters">
          <span>Try another status or clear a date.</span>
        </EmptyState>
      )}
      {list.status === "ready" && list.items.length > 0 && (
        <>
          <div className="tableView warranty-ops-table">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Frame number</th>
                  <th scope="col">Part</th>
                  <th scope="col">Covered or paid</th>
                  <th scope="col" className="warranty-num">
                    Amount
                  </th>
                  <th scope="col">Delivery</th>
                  <th scope="col">Status</th>
                  <th scope="col">OMS order</th>
                  <th scope="col">Ticket</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr
                    key={item.partOrderId}
                    className="warranty-row-link"
                    onClick={(event) => {
                      // A mouse shortcut only: keyboard users have the frame number link.
                      if (event.target.closest("a, button")) {
                        return;
                      }
                      navigate(chatOrderUrl(item));
                    }}
                  >
                    <td className="warranty-nowrap">{formatDate(item.createdAt)}</td>
                    <td className="warranty-nowrap">
                      <Link to={chatOrderUrl(item)}>{dash(item.frameNumber)}</Link>
                    </td>
                    <td className="warranty-nowrap">{partLabel(item.part)}</td>
                    <td className="warranty-nowrap">{coveredLabel(item.covered)}</td>
                    <td className="warranty-num warranty-nowrap">
                      {formatMinor(item.payableMinor, item.currency || "INR")}
                    </td>
                    <td className="warranty-nowrap">{deliveryKindLabel(item.deliveryKind)}</td>
                    <td>
                      <span className={`warranty-badge ${statusClass(item.status)}`}>
                        {partOrderStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="warranty-nowrap">{dash(item.omsOrderCode)}</td>
                    <td className="warranty-nowrap">{dash(item.ticketNumber)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="warranty-more">
            {list.moreError && (
              <span className="warranty-field-msg is-error" role="alert">
                {list.moreError}
              </span>
            )}
            {list.nextCursor && (
              <Button className="Addbtn" onClick={list.loadMore} disabled={list.loadingMore}>
                {list.loadingMore ? "Loading" : "Load more"}
              </Button>
            )}
          </div>
        </>
      )}
    </WarrantyPage>
  );
};

export default ChatOrders;
